import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MagnifyingGlass, Briefcase, CheckCircle, SealCheck } from "@phosphor-icons/react";
import { GoogleButton } from "@/components/GoogleButton";

const inputCls = "mt-1.5 h-12 rounded-[14px] border-ppc bg-ppc-bg text-ppc-text";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", role: "customer", is_business: false, business_name: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(form);
      toast.success(t("toast_saved"));
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const RoleRow = ({ value, icon: Icon, label }) => {
    const on = form.role === value;
    return (
      <button
        type="button"
        data-testid={`register-role-${value}`}
        onClick={() => set("role", value)}
        className="flex w-full items-center gap-3.5 rounded-2xl p-4 text-left transition-[border-color,box-shadow,background-color] duration-150"
        style={
          on
            ? {
                border: "1px solid rgba(145,132,217,0.55)",
                background: "linear-gradient(180deg, rgba(145,132,217,0.16), rgba(145,132,217,0.05))",
                boxShadow: "0 14px 34px rgba(0,0,0,0.45), 0 0 0 4px rgba(145,132,217,0.07)",
              }
            : { border: "1px solid rgba(255,255,255,0.07)", background: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))" }
        }
      >
        <Icon size={20} color={on ? "#9184d9" : "rgba(233,233,237,0.6)"} />
        <span className="flex-1 text-[15px] text-ppc-text">{label}</span>
        {on && <CheckCircle size={20} weight="fill" color="#9184d9" />}
      </button>
    );
  };

  return (
    <div className="relative mx-auto flex min-h-[80vh] max-w-md flex-col px-6 pb-28 pt-12 md:pb-16">
      <h1 className="text-4xl leading-[1.08] tracking-[-0.02em] text-ppc-text sm:text-5xl">{t("register_title")}</h1>
      <p className="mt-2.5 text-sm text-ppc-muted">{t("hero_sub")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <p className="label-caps mb-2.5">{t("i_want_to")}</p>
          <div className="space-y-2.5">
            <RoleRow value="customer" icon={MagnifyingGlass} label={t("find_services")} />
            <RoleRow value="provider" icon={Briefcase} label={t("offer_services")} />
          </div>
        </div>

        <div>
          <Label htmlFor="name" className="text-ppc-muted">{t("name")}</Label>
          <Input id="name" data-testid="register-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required className={inputCls} />
        </div>
        <div>
          <Label htmlFor="email" className="text-ppc-muted">{t("email")}</Label>
          <Input id="email" type="email" data-testid="register-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} required className={inputCls} />
        </div>
        <div>
          <Label htmlFor="password" className="text-ppc-muted">{t("password")}</Label>
          <Input id="password" type="password" data-testid="register-password-input" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="phone" className="text-ppc-muted">{t("phone")}</Label>
          <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
        </div>

        <label className="flex items-center gap-3 rounded-[14px] border border-ppc bg-ppc-bg p-3.5">
          <Checkbox data-testid="register-business-checkbox" checked={form.is_business} onCheckedChange={(v) => set("is_business", !!v)} />
          <span className="text-sm text-ppc-text">{t("business_account")}</span>
        </label>
        {form.is_business && (
          <div>
            <Label htmlFor="bn" className="text-ppc-muted">{t("business_name")}</Label>
            <Input id="bn" data-testid="register-business-name-input" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} />
          </div>
        )}

        {err && <p className="inline-error" data-testid="register-error">{err}</p>}

        <Button type="submit" disabled={busy} data-testid="register-submit-btn" className="btn-primary h-[50px] w-full rounded-[14px] text-[15px]">
          {t("sign_up")}
        </Button>
        <GoogleButton label={t("google_btn")} />
      </form>

      <p className="mt-6 text-center text-sm text-ppc-muted">
        {t("have_account")}{" "}
        <Link to="/login" data-testid="go-login-link" className="text-primary-green">{t("sign_in")}</Link>
      </p>
      <p className="mt-auto flex items-center justify-center gap-1.5 pt-8 text-xs text-ppc-muted">
        <SealCheck size={15} weight="fill" color="#9184d9" /> Stripe · {t("app_tagline")}
      </p>
    </div>
  );
}
